export const flatten = <T>(nestedArrays: T[][]) => nestedArrays.reduce((r, e) => r.concat(e), []);

export function intersection<T>(left: T[], right: T[]): T[] {
    if (!right || !left) {
        return [];
    }
    return left.filter(value => right.indexOf(value) > -1);
}

export function range(length: number): number[] {
    const result = [];
    for (let i = 0; i < length; i++) {
        result.push(i);
    }
    return result;
}

export type MapOfSet = { [element: string]: boolean };
export function createMapOfSet(array: (string | number)[]): MapOfSet {
    const map = {};
    (array || []).forEach(element => (map[element] = true));
    return map;
}

export function addOrRemoveElement<T>(array: T[], element: T): T[] {
    const index = array.indexOf(element);

    if (index === -1) {
        return [...array, element];
    } else {
        return removeElementAtIndex(array, index);
    }
}

export function removeElementAtIndex<T>(array: T[], index: number): T[] {
    return [...array.slice(0, index), ...array.slice(index + 1)];
}

export function arraysAreEqual<T>(left: T[], right: T[]): boolean {
    if (!left && !right) {
        return true;
    }

    if (!left !== !right) {
        return false;
    }

    left = left || [];
    right = right || [];

    const leftInRight = left.every(elem => right.includes(elem));
    const rightInLeft = right.every(elem => left.includes(elem));

    return leftInRight && rightInLeft;
}

export function chunkArrayByRunLength<T>(array: T[], runLength: number): T[][] {
    if (!array.length) {
        return [];
    }

    return array.reduce(
        (result, value, index) => {
            if (!Math.floor(index % runLength) && index) {
                result.push([]);
            }
            result[result.length - 1].push(value);
            return result;
        },
        [[]] as T[][]
    );
}

export function chunkArrayByChunkCount<T>(array: T[], chunkCount: number): T[][] {
    const runLength = array.length / chunkCount;
    return chunkArrayByRunLength(array, runLength);
}

export function uniqBy<T>(array: T[], mappingFunction: (element: T) => string | number): T[] {
    const seenKeys = {};
    const output: T[] = [];
    array.forEach(element => {
        const key = mappingFunction(element);
        if (!seenKeys.hasOwnProperty(key)) {
            output.push(element);
            seenKeys[key] = true;
        }
    });

    return output;
}

export function uniq<T extends number | string>(array: T[]): T[] {
    return uniqBy(array, e => e);
}

export function groupBy<T>(input: T[], getKey: (t: T) => string | number): { [key: string]: T[] } {
    return input.reduce((result, element) => {
        const key = getKey(element);
        if (!result.hasOwnProperty(key)) {
            result[key] = [];
        }

        result[key].push(element);
        return result;
    }, {});
}

/**
 * Returns the total sum for numbers in an array
 * @param array An array holding numbers
 */
export function sum(array: number[]): number {
    return array ? array.reduce((v1, v2) => v1 + v2, 0) : 0;
}

export function take<T>(array: T[], count: number, predicate: (t: T) => boolean): T[] {
    if (!predicate) {
        return array.slice(0, count);
    }

    const result = [];

    const arrayLength = array.length;
    for (let i = 0; i < arrayLength; ++i) {
        const item = array[i];
        if (predicate(item)) {
            if (result.push(item) >= count) {
                return result;
            }
        }
    }

    return result;
}
